// src/App.js - COMPLETE VERSION WITH ANIMATED CSS TRANSITIONS AND AUTH TOKEN REDIRECT
import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { supabase } from './backend/supabase';

// Import animated CSS slice hook
import useCSSSlice from './lib/useCSSSlice';

// ========================================================================
// IMPORTS
// ========================================================================
import AuthCallback from './components/auth/AuthCallback';
import ComingSoonPage from './ComingSoonPage';
import SignupPage from './SignupPage';
import RegistrationForm from './components/signup/RegistrationForm';
import LoginForm from './components/signup/LoginForm';
import PendingApproval from './components/signup/PendingApproval';
import LinkedInCallback from './LinkedInCallback';
import GoogleCallback from './GoogleCallback';
import OneDriveCallback from './OneDriveCallback.js';
import PrivacyPolicy from './components/landing/PrivacyPolicy.js';
import TermsOfService from './components/landing/TermsOfService.js';
import LegalSearchBeta from './LegalSearchBeta';

// Lazily loaded imports...
const AttorneyProfileCompletion = lazy(() => import('./components/signup/AttorneyProfileCompletion'));
const Home = lazy(() => import('./Home'));
const Dashboard = lazy(() => import('./Dashboard'));
const ReferralForm = lazy(() => import('./components/referrals/ReferralForm'));
// const CompanyPage = lazy(() => import('./companypage')); // TEMPORARILY DISABLED
const LienDocManager = lazy(() => import('./components/lien/LienDocManager'));
const LienCreationPage = lazy(() => import('./components/lien/LienCreationForm'));
const KiwiAI = lazy(() => import('./components/chatbot/Kiwi-AI'));
const LienEditPage = lazy(() => import('./components/lien/LienEditPage'));
const EmailTest = lazy(() => import('./components/test/EmailTest'));
const FileDownloadHandler = lazy(() => import('./components/chatbot/FileDownloadHandler'));
const SettingsPage = lazy(() => import('./components/other/Settings'));
const NotificationsPage = lazy(() => import('./components/other/Notifications'));
const SupportTicket = lazy(() => import('./components/other/SupportTicket'));
const Home2 = lazy(() => import('./Home2'));
const ExplorePage = lazy(() => import('./components/dashboard(main)/Explore'));
const ExploreDetailPage = lazy(() => import('./components/dashboard(main)/ExploreDetailPage'));
const SavedCases = lazy(() => import('./components/dashboard(main)/SavedCases'));
const ChatbotInterface = lazy(() => import('./components/chatbot/uitesting.js'));
const PrivateNetwork = lazy(() => import('./components/dashboard(main)/PrivateNetwork'));
const CallCenter = lazy(() => import('./components/call/CallCenterDashboard.js'));

// ADD FORGOT PASSWORD IMPORTS
const ForgotPassword = lazy(() => import('./components/signup/forgotpassword/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/signup/forgotpassword/ResetPassword'));

// LAZY LOAD CLAY - this is key to prevent CSS conflicts
const ClayLanding = lazy(() => import('./clay_landing'));

// Loading fallback components
const LoadingFallback = () => (
  <div className="min-h-screen bg-[#e9e4dc] flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black"></div>
  </div>
);

const InitializingState = () => (
  <div className="min-h-screen bg-[#d8d1c7] flex items-center justify-center">
    <span className="text-[#3d3731]">Initializing App...</span>
  </div>
);

// Enhanced transition loading indicator
const TransitionLoader = ({ isTransitioning }) => {
  if (!isTransitioning) return null;
  
  return (
    <div style={{
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: 'rgba(52, 38, 30, 0.95)',
      color: 'white',
      padding: '12px 20px',
      borderRadius: '25px',
      fontSize: '13px',
      zIndex: 1000000,
      fontFamily: 'Inter, sans-serif',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      animation: 'slideInRight 0.3s ease-out'
    }}>
      <div style={{
        width: '14px',
        height: '14px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: '2px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }}></div>
      ✨ Switching styles...
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes slideInRight {
          0% { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// Temporary "Coming Soon" component for disabled routes
const TemporarilyDisabled = ({ routeName }) => (
  <div className="min-h-screen bg-[#e9e4dc] flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-4xl font-bold text-[#3d3731] mb-4">Coming Soon</h1>
      <p className="text-xl text-[#a39b8f] mb-8">{routeName} is temporarily disabled while we make improvements.</p>
      <button 
        onClick={() => window.history.back()} 
        className="px-6 py-3 bg-black text-white rounded-lg hover:bg-[#3d3731] transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

// Custom hook for auth token redirect
const useAuthTokenRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Check for auth tokens in URL hash
    const hashParams = new URLSearchParams(location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    // If we have recovery tokens but we're not on the reset-password page
    if (accessToken && type === 'recovery' && location.pathname !== '/reset-password') {
      console.log('[Auth Redirect] Recovery tokens detected, redirecting to /reset-password');
      navigate(`/reset-password${location.hash}`, { replace: true });
    }
  }, [location, navigate]);
};

// ProtectedRoute component remains the same
const ProtectedRoute = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const mountedRef = useRef(true);
  const navigate = useNavigate();

  useEffect(() => {
    mountedRef.current = true;
    console.log(`[ProtectedRoute] Effect running. Path: ${window.location.pathname}. isLoading: ${isLoading}, isAuthenticated: ${isAuthenticated}`);

    const checkAuth = (session) => {
      if (!mountedRef.current) return;
      if (session) {
        console.log('[ProtectedRoute] Session present. Authenticated.');
        setIsAuthenticated(true);
      } else {
        console.log('[ProtectedRoute] No session. Not authenticated.');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      console.log('[ProtectedRoute] Initial getSession returned:', currentSession ? 'Session' : 'No Session');
      if (mountedRef.current && isLoading && isAuthenticated === null) {
        checkAuth(currentSession);
      }
    }).catch(error => {
        if(mountedRef.current && isLoading && isAuthenticated === null) {
            console.error('[ProtectedRoute] Error in initial getSession:', error);
            setIsAuthenticated(false);
            setIsLoading(false);
        }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mountedRef.current) return;
      console.log(`[ProtectedRoute] onAuthStateChange event: ${event}`, session ? 'Session Present' : 'No Session');
      checkAuth(session);
    });

    return () => {
      mountedRef.current = false;
      console.log('[ProtectedRoute] Cleanup effect.');
      subscription?.unsubscribe();
    };
  }, []);

  if (isLoading || isAuthenticated === null) {
    console.log('[ProtectedRoute] Rendering: Loading/Initializing...');
    return (
      <div className="min-h-screen bg-[#d8d1c7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3d3731]"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    console.log('[ProtectedRoute] Rendering: Authenticated. Rendering children for path:', window.location.pathname);
    return <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;
  }

  console.log('[ProtectedRoute] Rendering: Not Authenticated. Redirecting to /signup/login for path:', window.location.pathname);
  if (window.location.pathname === '/signup/login') {
    return null;
  }
  return <Navigate to="/signup/login" replace />;
};

// Component to handle routing WITH ANIMATED CSS TRANSITIONS
function AppContent() {
  const location = useLocation();
  
  // Use the auth token redirect hook
  useAuthTokenRedirect();

  // Add this right after useAuthTokenRedirect();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
    
  // IMMEDIATE CSS MODE SWITCHING - This runs BEFORE animations
  React.useEffect(() => {
    const clayRoutes = ['/', '/pricing', '/company', '/privacy-policy', '/terms-of-service', '/legal-search-beta'];
    const signupRoutes = ['/signup', '/signup/login', '/signup/register', '/pending-approval', '/forgot-password', '/reset-password'];
    
    const isClayRoute = clayRoutes.includes(location.pathname);
    const isSignupRoute = signupRoutes.some(route => location.pathname.startsWith(route));
    
    console.log(`[AppContent] Route change to: ${location.pathname}`);
    console.log(`[AppContent] Is Clay: ${isClayRoute}, Is Signup: ${isSignupRoute}`);
    
    // Determine the mode and apply it
    let mode = 'app';
    let className = 'app-mode';
    
    if (isClayRoute) {
      mode = 'clay';
      className = 'clay-mode';
    } else if (isSignupRoute) {
      mode = 'signup';
      className = 'signup-mode';
    }
    
    // ALWAYS set the correct classes for ALL routes
    console.log(`[AppContent] Setting mode to: ${mode}`);
    document.documentElement.className = className;
    document.body.className = className;
    document.documentElement.setAttribute('data-mode', mode);
    
    // Now handle CSS management based on route type
    if (mode === 'app') {
      // For app routes (dashboard, etc), ensure Clay styles are disabled
      console.log('[AppContent] App mode - ensuring Clay styles are disabled');
      
      // Re-enable any critical CSS that might have been disabled
      const criticalCSS = Array.from(document.querySelectorAll('style')).find(
        style => style.textContent?.includes('html.signup-mode')
      );
      if (criticalCSS) {
        criticalCSS.disabled = false;
      }
      
      // Disable any Clay styles that might be active
      const clayStyles = document.querySelectorAll('[data-clay-disabled]');
      clayStyles.forEach(style => {
        if (!style.disabled) {
          style.disabled = true;
          console.log('[AppContent] Disabled lingering Clay style');
        }
      });
      
      return; // No further CSS management needed for app routes
    }
    
    if (isSignupRoute) {
      console.log('[AppContent] Switching to signup mode');
      
      // CRITICAL FIX: Don't disable the critical CSS from index.html!
      const allStyles = document.querySelectorAll('style, link[rel="stylesheet"]');
      let disabledCount = 0;
      
      allStyles.forEach((style, index) => {
        const content = style.textContent || '';
        const href = style.href || '';
        
        // Skip the critical CSS that contains signup-mode styles
        if (content.includes('html.signup-mode') && content.includes('background-color: #fef6ef')) {
          console.log(`[AppContent] Keeping critical CSS (style ${index})`);
          style.disabled = false; // Make sure it's enabled!
          return;
        }
        
        // Only keep main.css - remove chunk CSS files that contain Clay styles
        if (href.includes('/static/css/main')) {
          console.log(`[AppContent] Keeping main CSS file: ${href}`);
          return;
        }
        
        // Only disable CSS files, NOT JavaScript files
        if (href.includes('/static/css/') && href.includes('.chunk.css')) {
          console.log(`[AppContent] Disabling chunk CSS file: ${href}`);
          style.disabled = true;
          style.setAttribute('data-clay-disabled', 'true');
          style.setAttribute('data-disabled-reason', 'chunk-css');
          disabledCount++;
          return;
        }
        
        // Skip JavaScript files completely
        if (href.includes('.js') || href.includes('.mjs')) {
          return;
        }
        
        // Skip the CSS transition animations
        if (content.includes('css-transition-overlay')) {
          return;
        }
        
        // Disable Clay inline styles
        if (!href && style.tagName === 'STYLE') {
          const clayPatterns = [
            'scrolling-features', 'homepage-badge', 'nav2_', 
            '.col:has', '@keyframes slideIn', '@media (max-width: 991px)',
            'demo-link', 'login-link', 'clay-landing-wrapper'
          ];
          
          const isClayStyle = clayPatterns.some(pattern => content.includes(pattern));
          
          if (isClayStyle) {
            console.log(`[AppContent] Disabling Clay inline style ${index}`);
            style.disabled = true;
            style.setAttribute('data-clay-disabled', 'true');
            style.setAttribute('data-disabled-reason', 'clay-inline');
            disabledCount++;
          }
        }
      });
      
      console.log(`[AppContent] Disabled ${disabledCount} Clay styles total`);
      
    } else if (isClayRoute) {
      console.log('[AppContent] Switching to clay mode');
      
      // Re-enable ALL disabled Clay styles
      const disabledStyles = document.querySelectorAll('[data-clay-disabled="true"]');
      let enabledCount = 0;
      
      disabledStyles.forEach(style => {
        style.disabled = false;
        style.removeAttribute('data-clay-disabled');
        style.removeAttribute('data-disabled-reason');
        enabledCount++;
        console.log(`[AppContent] Re-enabled style: ${style.href || 'inline'}`);
      });
      
      // Make sure critical CSS is enabled
      const criticalCSS = Array.from(document.querySelectorAll('style')).find(
        style => style.textContent?.includes('html.signup-mode')
      );
      if (criticalCSS) {
        criticalCSS.disabled = false;
      }
      
      console.log(`[AppContent] Re-enabled ${enabledCount} Clay styles`);
    }
  }, [location.pathname]); // Run on every route change
  
  // USE ANIMATED CSS SLICE HOOK
  // Animation options: 'slide', 'wipe', 'blur', 'glitch', 'matrix', 'auto'
  const { isTransitioning } = useCSSSlice('auto'); // Smart auto-selection
  
  // Add debug logging (remove in production)
  React.useEffect(() => {
    console.log(`[AppContent] Path: ${location.pathname}, Transitioning: ${isTransitioning}`);
  }, [location.pathname, isTransitioning]);
  
  // Define which routes should use Clay styling
  const clayRoutes = ['/', '/pricing', '/company', '/privacy-policy', '/terms-of-service', '/legal-search-beta'];
  const signupRoutes = ['/signup', '/signup/login', '/signup/register', '/pending-approval', '/forgot-password', '/reset-password'];
  
  const isClayRoute = clayRoutes.includes(location.pathname);
  const isSignupRoute = signupRoutes.some(route => location.pathname.startsWith(route));
  
  // Determine the app mode - clay, signup, or app (default)
  let appMode = 'app';
  let appClass = 'app-mode';
  
  if (isClayRoute) {
    appMode = 'clay';
    appClass = 'clay-mode';
  } else if (isSignupRoute) {
    appMode = 'signup';
    appClass = 'signup-mode';
  }
  
  return (
    <>
      {/* Show cool transition loader during CSS switches */}
      <TransitionLoader isTransitioning={isTransitioning} />
      
      {/* Wrap everything with enhanced styling */}
      <div 
        data-app-mode={appMode}
        className={appClass}
        style={{
          minHeight: '100vh',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative'
        }}
      >
        {/* If it's a Clay route, render ClayLanding with lazy loading */}
        {isClayRoute ? (
          <Suspense fallback={<LoadingFallback />}>
            <ClayLanding />
          </Suspense>
        ) : (
          /* Otherwise, render the regular Tailwind-based routes with proper ID scoping */
          <div id="tailwind-root">
            <Routes>
              {/* Public routes */}
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/signup/register" element={<RegistrationForm />} />
              <Route path="/signup/login" element={<LoginForm />} />
              <Route path="/onedrive-callback" element={<OneDriveCallback />} />
              
              {/* FORGOT PASSWORD ROUTES - ADD THESE */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              
              {/* OAuth Callback Routes */}
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route path="/linkedin-callback" element={<LinkedInCallback />} />
              <Route path="/google-callback" element={<GoogleCallback />} />
              
              {/* Other Public Routes */}
              <Route path="/home" element={<Home />} />
              <Route path="/legal-search-beta" element={<LegalSearchBeta />} />
              <Route path="/ui-test" element={<ChatbotInterface />} />
              
              {/* TEMPORARILY DISABLED ROUTES */}
              <Route path="/coming-soon" element={<Navigate to="/legal-search-beta" replace />} />
              
              {/* Protected Routes */}
              <Route path="/pending-approval" element={<ProtectedRoute><PendingApproval /></ProtectedRoute>} />
              <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/submit-referral" element={<ProtectedRoute><ReferralForm /></ProtectedRoute>} />
              <Route path="/kiwi-ai" element={<ProtectedRoute><KiwiAI /></ProtectedRoute>} />
              <Route path="/test/email" element={<ProtectedRoute><EmailTest /></ProtectedRoute>} />
              <Route path="/api/download-file" element={<ProtectedRoute><FileDownloadHandler /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/support" element={<ProtectedRoute><SupportTicket /></ProtectedRoute>} />
              <Route path="/explore" element={<ProtectedRoute><ExplorePage /></ProtectedRoute>} />
              <Route path="/saved-cases" element={<ProtectedRoute><SavedCases /></ProtectedRoute>} />
              <Route path="/explore/:id" element={<ProtectedRoute><ExploreDetailPage /></ProtectedRoute>} />
              <Route path="/home2" element={<ProtectedRoute><Home2 /></ProtectedRoute>} />
              <Route path="/private-network" element={<ProtectedRoute><PrivateNetwork /></ProtectedRoute>} />
              <Route path="/call-center" element={<ProtectedRoute><CallCenter /></ProtectedRoute>} />
              <Route path="/dashboard/liens/new" element={<ProtectedRoute><LienCreationPage /></ProtectedRoute>} />
              <Route path="/dashboard/liens/:id/edit" element={<ProtectedRoute><LienEditPage /></ProtectedRoute>} />
              <Route path="/dashboard/liens/document/new" element={<ProtectedRoute><LienDocManager /></ProtectedRoute>} />
            </Routes>
          </div>
        )}
      </div>
    </>
  );
}

function App() {
  const [initialized, setInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('App.js: Auth attempting initialization.');
      } catch (error) {
        console.error('App.js: Unexpected auth initialization error:', error);
        setInitError(error.message);
      } finally {
        setInitialized(true);
      }
    };
    initializeAuth();
  }, []);

  if (!initialized) { return <InitializingState />; }

  if (initError) {
     return (
        <div className="min-h-screen bg-[#e9e4dc] flex flex-col items-center justify-center p-4">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative max-w-md w-full">
                <strong className="font-bold">Failed to initialize:</strong>
                <span className="block sm:inline ml-1">{initError}</span>
                <p className="mt-2">Please refresh the page or try again later.</p>
            </div>
        </div>
    );
  }

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!googleClientId) {
      console.error("FATAL ERROR: REACT_APP_GOOGLE_CLIENT_ID is not defined.");
      return (
        <div className="min-h-screen bg-[#e9e4dc] flex flex-col items-center justify-center p-4">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative max-w-md w-full">
                <strong className="font-bold">Configuration Error:</strong>
                <span className="block sm:inline ml-1">Google Client ID is missing.</span>
            </div>
        </div>
       );
  }

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <AppContent />
        </Suspense>
      </Router>
    </GoogleOAuthProvider>
  );
}

export default App;